import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import type { U2000AutoCompleteSearchResult } from "../../../u2000Core/types/U2000AutoCompleteSearchResult";
import { u2000ExtractAutoCompleteResults } from "../../../u2000Core/utils/u2000ExtractAutoCompleteResults";
import {
  doFindSearchCadastres,
  doFindSearchDistricts,
  doFindSearchLots,
  luHandleAppiError,
  type U3142AutoCompletCriterias,
  type U3142FindSearchCadastreItem,
  type U3142FindSearchDistrictItem,
  type U3142FindSearchLotItem,
} from "../../U3142Service";
import { NS } from "../../U3142";
import { u3142MapFindSearchLotsCriterias } from "../../tools/u3142CompactLotForSearch";

export { u3142FindSearchCadastreItemToDisplay } from "./U3142FindSearchCadastreDisplay";

/** Autocomplete critère liste — POST /U3142/findSearchLots (BL-0067). */
export function useU3142FindSearchLotsHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3142AutoCompletCriterias): Promise<U2000AutoCompleteSearchResult<U3142FindSearchLotItem>> => {
      try {
        const response = await doFindSearchLots(axios, u3142MapFindSearchLotsCriterias(criterias));
        return u2000ExtractAutoCompleteResults<U3142FindSearchLotItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast],
  );

  return { search };
}

/** Autocomplete critère liste — POST /U3142/findSearchDistricts (BL-0067). */
export function useU3142FindSearchDistrictsHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3142AutoCompletCriterias): Promise<U2000AutoCompleteSearchResult<U3142FindSearchDistrictItem>> => {
      try {
        const response = await doFindSearchDistricts(axios, criterias);
        return u2000ExtractAutoCompleteResults<U3142FindSearchDistrictItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast],
  );

  return { search };
}

/** Autocomplete critère liste — POST /U3142/findSearchCadastres (BL-0067). */
export function useU3142FindSearchCadastresHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3142AutoCompletCriterias): Promise<U2000AutoCompleteSearchResult<U3142FindSearchCadastreItem>> => {
      try {
        const response = await doFindSearchCadastres(axios, criterias);
        return u2000ExtractAutoCompleteResults<U3142FindSearchCadastreItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast],
  );

  return { search };
}

